import { Application } from '@frontend/functionality/types';
import { getApplicationDisplayName } from '@frontend/functionality/utils';
import { CommonMessage } from '@frontend/lang';
import { LuOctagonAlert, LuTriangleAlert, LuX } from 'react-icons/lu';
import { FormattedMessage, useIntl } from 'react-intl';

import { BundleUsageBannerState } from './application-bundle-usage.utils';

const ID = 'application-bundle-usage-banner';

export interface ApplicationBundleUsageBannerProps {
    application: Application;
    banner: BundleUsageBannerState;
    onDismiss: () => void;
}

export const ApplicationBundleUsageBanner = ({ application, banner, onDismiss }: ApplicationBundleUsageBannerProps) => {
    const intl = useIntl();
    const isCritical = banner.severity === 'critical';
    const messageValues = {
        application: getApplicationDisplayName(application),
        percent: banner.percent
    };

    return (
        <div
            id={ID}
            role='status'
            className={`fixed bottom-4 left-1/2 z-[60] flex w-[min(100%-2rem,36rem)] -translate-x-1/2 items-center gap-3 rounded-md border px-4 py-3 shadow-lg ${
                isCritical ? 'bg-status-danger/20 border-status-danger' : 'bg-status-warning/20 border-status-warning'
            }`}>
            {isCritical ? (
                <LuOctagonAlert className='text-status-danger size-5 shrink-0' />
            ) : (
                <LuTriangleAlert className='text-status-warning size-5 shrink-0' />
            )}
            <div className='text-foreground-default min-w-0 flex-1 text-center text-sm'>
                {isCritical ? (
                    <FormattedMessage
                        id='platform.components.application-bundle-usage-banner.critical'
                        description='Critical banner when verified bundle usage is at or above 100% for the current application'
                        defaultMessage='You have used {percent}% of your {application} bundle.'
                        values={messageValues}
                    />
                ) : (
                    <FormattedMessage
                        id='platform.components.application-bundle-usage-banner.warning'
                        description='Warning banner when verified bundle usage is between 80% and 99% for the current application'
                        defaultMessage='You are currently using {percent}% of your {application} bundle.'
                        values={messageValues}
                    />
                )}
            </div>
            <button
                type='button'
                className='text-brand-muted hover:text-foreground-default shrink-0 rounded-md p-1 hover:cursor-pointer'
                aria-label={intl.formatMessage(CommonMessage.BUTTONS.CLOSE.props)}
                onClick={onDismiss}>
                <LuX className='size-5' />
            </button>
        </div>
    );
};
